/**
 * Plan change confirmation text (WP-BILLING-19 Part B review L-4, decision
 * 2026-09-27 "Confirm with amount"). Mirrors clients/mobile/src/utils/planChange.ts.
 *
 * Kept out of ChangePlanModal.tsx: a component file that also exports
 * functions breaks React fast refresh (react-refresh/only-export-components).
 */
import { formatDate } from '@/utils/format';
import type { Plan, PlanInterval, Subscription } from '@/types/billing';

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
}

export function formatPlanPrice(amount: number, currency: string, interval: PlanInterval): string {
  return `${formatAmount(amount, currency)}/${interval === 'monthly' ? 'month' : 'year'}`;
}

/**
 * What the change costs, matching StripeClient.change_subscription_price
 * (proration_behavior=create_prorations):
 * - trialing: nothing now; the trial keeps its end date.
 * - interval change: Stripe starts a new billing period today and invoices it
 *   at once, less a credit for the unused time.
 * - same interval: the prorated difference goes on the next invoice.
 */
export function describePlanChange(subscription: Subscription, plan: Plan): string {
  const newPrice = formatPlanPrice(plan.amount, plan.currency, plan.interval);
  if (subscription.status === 'trialing') {
    return (
      `Your free trial continues until ${formatDate(subscription.trialEnd)} and nothing is charged now. ` +
      `After the trial you will pay ${newPrice}.`
    );
  }
  if (plan.interval !== subscription.planInterval) {
    return (
      `Your billing period restarts today: your card is charged ${formatAmount(plan.amount, plan.currency)} now, ` +
      `less a credit for the unused part of your current plan. After that you pay ${newPrice}.`
    );
  }
  return (
    `The difference for the rest of this billing period (a charge or a credit) is added to your next invoice. ` +
    `After that you pay ${newPrice}.`
  );
}
